import { useState } from "react";
import { ArrowLeft, Calendar } from "lucide-react";
import PageHero from "@/components/PageHero";
import CtaBand from "@/components/CtaBand";
import { NEWS } from "@/lib/data";

export default function News() {
  const [activeSlug, setActiveSlug] = useState(null);
  const active = NEWS.find((n) => n.slug === activeSlug);

  if (active) {
    return (
      <div data-testid="news-detail">
        <section className="pt-16 md:pt-24 pb-12 border-b border-slate-200" style={{ background: "var(--zt-bg-alt)" }}>
          <div className="zt-container max-w-4xl">
            <button
              data-testid="news-back-button"
              onClick={() => setActiveSlug(null)}
              className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-8"
            >
              <ArrowLeft size={16} /> Back to News
            </button>
            <p className="zt-overline mb-5 inline-flex items-center gap-2">
              <Calendar size={12} /> {active.date}
            </p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">{active.title}</h1>
            <p className="text-lg text-slate-600 mt-6 leading-relaxed">{active.excerpt}</p>
          </div>
        </section>
        <article className="zt-section">
          <div className="zt-container max-w-3xl space-y-6">
            {active.body.map((p) => (
              <p key={p.slice(0, 80)} className="text-lg text-slate-700 leading-[1.8]">{p}</p>
            ))}
            {active.url && (
              <p className="pt-4">
                <a
                  href={active.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="news-source-link"
                  className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider border-b pb-0.5 transition-colors hover:opacity-80"
                  style={{ color: "var(--zt-teal)", borderColor: "var(--zt-teal)" }}
                >
                  Read the Full Release ↗
                </a>
              </p>
            )}
            <p className="text-sm text-slate-500 pt-8 border-t border-slate-200">Source: {active.source}</p>
          </div>
        </article>
        <CtaBand />
      </div>
    );
  }

  return (
    <div data-testid="news-page">
      <PageHero
        eyebrow="Newsroom"
        title="Latest from ZT Health"
        subtitle="Press releases, acquisitions, and platform updates from the ZT Health and ZT Corporate portfolio."
      />

      <section className="zt-section">
        <div className="zt-container">
          <div className="space-y-px" style={{ background: "var(--zt-border)" }}>
            {NEWS.map((n, i) => (
              <button
                key={n.slug}
                data-testid={`news-item-${i}`}
                onClick={() => setActiveSlug(n.slug)}
                className="w-full text-left p-8 md:p-10 transition-all hover:bg-slate-50 group"
                style={{ background: "var(--zt-bg-alt)" }}
              >
                <div className="grid md:grid-cols-12 gap-6 items-start">
                  <div className="md:col-span-3">
                    <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "var(--zt-gold)" }}>
                      <Calendar size={12} className="inline mr-2" />
                      {n.date}
                    </p>
                  </div>
                  <div className="md:col-span-8">
                    <h2 className="text-xl md:text-2xl font-semibold leading-snug group-hover:underline decoration-2 underline-offset-4 mb-3" style={{ textDecorationColor: "var(--zt-navy)" }}>
                      {n.title}
                    </h2>
                    <p className="text-slate-600 leading-relaxed">{n.excerpt}</p>
                  </div>
                  <div className="md:col-span-1 flex md:justify-end">
                    <span
                      className="inline-flex items-center justify-center w-10 h-10 transition-colors"
                      style={{ background: "var(--zt-bg-muted)" }}
                    >
                      <span className="text-lg" style={{ color: "var(--zt-navy)" }}>→</span>
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div
            data-testid="news-full-archive"
            className="mt-12 md:mt-16 pt-10 border-t border-slate-200 text-center max-w-2xl mx-auto"
          >
            <p className="text-base md:text-lg text-slate-600 leading-relaxed">
              Looking for more? Visit the full ZT Corporate newsroom for our complete archive of press releases and announcements.
            </p>
            <a
              href="https://ztcorporate.com/news/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="news-archive-link"
              className="zt-btn-primary inline-flex items-center gap-2 mt-6"
            >
              Visit ZT Corporate News ↗
            </a>
          </div>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
